import { UpgradeStage, UpgradeStageStep } from 'api/models';
import { UpgradeAny, UpgradeViewMode } from '../helpers/UpgradesHelpers';
import { TabsWithContent } from 'components/design-system/Tabs';
import useLocalStorage from 'hooks/useLocalStorage';
import { Button, StepBase } from 'subframe/index';
import { CopyToClipboard } from 'components/design-system';

interface UpgradeStepAgentSidebarProps {
  upgrade: UpgradeAny;
  mode: UpgradeViewMode;
  stage: UpgradeStage;
  step: UpgradeStageStep;
  showExampleData: boolean;
}

function CopyToCursorTab(props: { upgradeId: string }) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <StepBase
        stepTitle="Open your IaC repo in Cursor"
        stepNumber="1"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <div className="flex flex-col items-start gap-2">
          <span className="text-body font-body text-default-font">
            Open your IaC code repo in Cursor
          </span>
          <div className="flex items-start gap-1">
            <Button
              variant="brand-tertiary"
              size="small"
              iconRight="FeatherExternalLink"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://docs.chkk.io/resources/installation-guides/cursor',
                  '_blank',
                  'noopener',
                );
              }}
            >
              Cursor Installation Guide
            </Button>
          </div>
        </div>
      </StepBase>
      <StepBase
        stepTitle="Ensure prerequisites are met"
        stepNumber="2"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <div className="flex flex-col items-start gap-3">
          <span className="whitespace-pre-wrap text-body font-body text-default-font">
            {
              '- Install Docker: Required to run the Chkk Upgrade Context MCP Server.\n- Install Chkk Upgrade Context MCP Server: Required to begin generating environment-aware pull requests using Chkk Agents'
            }
          </span>
          <Button
            variant="brand-tertiary"
            size="small"
            iconRight="FeatherExternalLink"
            onClick={(e) => {
              e.preventDefault();
              window.open(
                'https://docs.chkk.io/ai/cursor-plus-chkk',
                '_blank',
                'noopener',
              );
            }}
          >
            Quick Start Guide
          </Button>
        </div>
      </StepBase>
      <StepBase
        stepTitle="Copy prompt to Cursor"
        lastStep={true}
        stepNumber="3"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <CopyToClipboard
          text={`You’re working in a Cloud Native project’s IaC repository. Fetch the “addon-upgrade-agent” from Chkk MCP, and upgrade the package using the Upgrade Plan. The Upgrade Plan ID is ${props.upgradeId}`}
        />
      </StepBase>
    </div>
  );
}

function CopyToClaudeCodeTab(props: { upgradeId: string }) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <StepBase
        stepTitle="Open your IaC repo in Claude Code"
        stepNumber="1"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <div className="flex flex-col items-start gap-2">
          <span className="text-body font-body text-default-font">
            Open your IaC code repo in Claude Code
          </span>
          <div className="flex items-start gap-1">
            <Button
              variant="brand-tertiary"
              size="small"
              iconRight="FeatherExternalLink"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://docs.chkk.io/resources/installation-guides/claude-code',
                  '_blank',
                  'noopener',
                );
              }}
            >
              Claude Code Installation Guide
            </Button>
          </div>
        </div>
      </StepBase>
      <StepBase
        stepTitle="Ensure prerequisites are met"
        stepNumber="2"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <div className="flex flex-col items-start gap-3">
          <span className="whitespace-pre-wrap text-body font-body text-default-font">
            {
              '- Install Docker: Required to run the Chkk Upgrade Context MCP Server.\n- Install Chkk Upgrade Context MCP Server: Required to begin generating environment-aware pull requests using Chkk Agents'
            }
          </span>
          <Button
            variant="brand-tertiary"
            size="small"
            iconRight="FeatherExternalLink"
            onClick={(e) => {
              e.preventDefault();
              window.open(
                'https://docs.chkk.io/ai/claude-code-plus-chkk',
                '_blank',
                'noopener',
              );
            }}
          >
            Quick Start Guide
          </Button>
        </div>
      </StepBase>
      <StepBase
        stepTitle="Copy prompt to Claude Code"
        lastStep={true}
        stepNumber="3"
        stepTitleTooltip={<></>}
        defaultOpen={true}
      >
        <CopyToClipboard
          text={`You’re working in a Cloud Native project’s IaC repository. Fetch the “addon-upgrade-agent” from Chkk MCP, and upgrade the package using the Upgrade Plan. The Upgrade Plan ID is ${props.upgradeId}`}
        />
      </StepBase>
    </div>
  );
}

export function UpgradeStepAgentSidebar(props: UpgradeStepAgentSidebarProps) {
  const [tabId, setTabId] = useLocalStorage(
    'lcm_agent_tab',
    'export-to-cursor',
  );

  return (
    <div className="flex w-full min-w-[384px] grow shrink-0 basis-0 flex-col items-start justify-between mobile:w-full mobile:grow mobile:shrink-0 mobile:basis-0">
      <div>
        <div className="flex items-start gap-2 px-1 py-1">
          <span className="text-subheader font-subheader text-default-font ph-no-capture">
            Open IaC Pull Request with AI
          </span>
        </div>
        <TabsWithContent
          initialTabId={tabId}
          onTabChange={(newTabId) => setTabId(newTabId)}
          tabs={[
            {
              id: 'export-to-cursor',
              title: (
                <div className="flex gap-2">
                  <img className="w-4 flex-none" src="/icons/cursor.webp" />
                  <span>Export to Cursor</span>
                </div>
              ),
              content: <CopyToCursorTab upgradeId={props.upgrade.id} />,
            },
            {
              id: 'export-to-claude-code',
              title: (
                <div className="flex gap-2">
                  <img className="w-4 flex-none" src="/icons/claude.png" />
                  <span>Export to Claude Code</span>
                </div>
              ),
              content: <CopyToClaudeCodeTab upgradeId={props.upgrade.id} />,
            },
          ]}
        />
      </div>
      <span className="text-body font-body text-default-font">
        This action does not merge or deploy. It only creates a branch and opens
        a PR.
      </span>
    </div>
  );
}
